import { useCallback, useEffect, useMemo } from "react";
import { useAnalytics } from "../contexts/analytics/hooks";
import { trackErrorEvent } from "../utils/analytics";
import { formatPersonFullName } from "../utils/formatters";
import type { Nurse } from "../utils/models";
import { useLastNameFirst } from "./useNameFormat";
import { useDepartmentRosterQuery } from "./useNurses";

export type RosterLookup = {
	/** Undefined until the roster loads, and after a failure. */
	roster: Nurse[] | undefined;
	getNurseById: (id: number) => Nurse | undefined;
	getNurseFormattedName: (nurseId: number) => string;
	isLoading: boolean;
	isError: boolean;
	refetch: () => void;
};

/**
 * The department roster and lookups by id, without HospitalDataProvider. Reads the provider's own roster cache
 * entry, so it adds no request while the provider still loads the roster.
 *
 * Call it high in a page and pass the lookups down rather than calling it per cell: every call builds its own id map.
 * A failure is tracked here but not shown — each caller shows `isError` in its own error state, so a page that reads
 * the roster in two places doesn't stack two messages.
 */
export const useRosterLookup = (departmentId: string): RosterLookup => {
	const lastNameFirst = useLastNameFirst();
	const { trackError } = useAnalytics();
	const { data: roster, isLoading, isError, refetch: refetchRoster } = useDepartmentRosterQuery(departmentId);

	useEffect(() => {
		if (isError) trackError("department_roster_fetch_error", { department_id: departmentId });
	}, [isError, departmentId, trackError]);

	// DEFAULT: an empty map until the roster loads; pages wait on `isLoading` before building anything from it.
	const nursesById = useMemo(() => new Map((roster ?? []).map((nurse) => [nurse.id, nurse])), [roster]);

	const getNurseById = useCallback((id: number): Nurse | undefined => nursesById.get(id), [nursesById]);

	const getNurseFormattedName = useCallback(
		(nurseId: number): string => {
			const nurse = nursesById.get(nurseId);
			if (!nurse) {
				trackErrorEvent("invalid_nurse_id", `getNurseFormattedName: Nurse not found for id ${nurseId}`, { nurse_id: nurseId });
				return "";
			}
			return formatPersonFullName(nurse, lastNameFirst);
		},
		[nursesById, lastNameFirst],
	);

	const refetch = useCallback(() => void refetchRoster(), [refetchRoster]);

	return { roster, getNurseById, getNurseFormattedName, isLoading, isError, refetch };
};
